<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '../../../../components/Modal.vue';
import MessageTemplateForm from './MessageTemplateForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
// v6.4.18: PlatformTooltipIcon para tooltips dinâmicas
import PlatformTooltipIcon from 'dashboard/components-next/PlatformTooltipIcon.vue';

const props = defineProps({
  funnels: {
    type: Array,
    default: () => [],
  },
  selectedFunnelId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();
const emit = defineEmits(['switch-view', 'template-created']);

const showNewTemplateModal = ref(false);

const defaultInitialData = computed(() => {
  if (!props.funnels.length) {
    return {};
  }

  const funnel =
    props.funnels.find(
      funnelItem => String(funnelItem.id) === String(props.selectedFunnelId)
    ) || props.funnels[0];

  const stages = Object.entries(funnel.stages || {}).map(([id, stage]) => ({
    id: String(id),
    name: stage.name,
    position: stage.position ?? 0,
  }));

  const firstStage = stages.sort((a, b) => a.position - b.position)[0];

  return {
    funnel_id: String(funnel.id),
    stage_id: firstStage?.id || '',
  };
});

const handleBack = () => {
  emit('switch-view', 'kanban');
};

const handleTemplateCreated = template => {
  showNewTemplateModal.value = false;
  emit('template-created', template);
};
</script>

<template>
  <header class="message-templates-header">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-4">
        <Button variant="ghost" color="slate" size="sm" @click="handleBack">
          <fluent-icon icon="chevron-left" size="16" />
        </Button>
        <h1 class="text-2xl font-medium flex items-center gap-2">
          {{ t('KANBAN.MESSAGE_TEMPLATES.TITLE') }}
          <!-- v6.4.18: Tooltip de Modelos de Mensagens -->
          <PlatformTooltipIcon identifier="kanban_templates" size="sm" />
        </h1>
      </div>
      <Button variant="solid" color="blue" size="sm" @click="showNewTemplateModal = true">
        <fluent-icon icon="add" size="16" class="mr-2" />
        {{ t('KANBAN.MESSAGE_TEMPLATES.ADD') }}
      </Button>
    </div>

    <Modal
      v-model:show="showNewTemplateModal"
      :on-close="() => (showNewTemplateModal = false)"
      class="template-modal"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('KANBAN.MESSAGE_TEMPLATES.NEW') }}
        </h3>
        <MessageTemplateForm
          :funnels="funnels"
          :initial-data="defaultInitialData"
          @saved="handleTemplateCreated"
          @close="showNewTemplateModal = false"
        />
      </div>
    </Modal>
  </header>
</template>

<style lang="scss" scoped>
.message-templates-header {
  padding: var(--space-normal);
  border-bottom: 1px solid var(--color-border);
  background: var(--white);

  @apply dark:border-slate-800 dark:bg-slate-900;

  h1 {
    @apply dark:text-slate-100;
  }
}

:deep(.template-modal) {
  .modal-container {
    @apply max-w-[1024px] w-[90vw] dark:bg-slate-900;

    h3 {
      @apply dark:text-slate-100;
    }
  }
}
</style>
